import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { quoteRequestService } from '../../services/quoteRequestService';
import { salesLeadService } from '../../services/salesLeadService';
import { salesPortalService } from '../../services/salesPortalService';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  QUOTE_REQUEST_STATUS_META,
  SalesPageHeader,
  SalesSectionCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const emptyItem = () => ({
  item_type: 'part',
  description: '',
  quantity: 1,
  unit_price: 0,
  unit: 'EA',
  notes: '',
});

const EDITABLE = ['draft', 'rejected', 'withdrawn'];

const SalesQuoteRequestForm = () => {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [targetType, setTargetType] = useState(
    searchParams.get('customer_id') ? 'customer' : 'lead'
  );
  const [form, setForm] = useState({
    lead_id: searchParams.get('lead_id') || '',
    customer_id: searchParams.get('customer_id') || '',
    description: '',
    notes: '',
    requested_total_amount: '',
    needed_by_date: '',
    status: 'draft',
    review_notes: '',
    items: [emptyItem()],
  });

  const editable = isNew || EDITABLE.includes(form.status);

  const loadLookups = useCallback(async () => {
    try {
      const [leadRes, custRes] = await Promise.all([
        salesLeadService.getAll({ limit: 200 }),
        salesPortalService.getCustomers({ limit: 200 }),
      ]);
      setLeads(leadRes.data || []);
      setCustomers(custRes.data || []);
    } catch {
      /* lookups optional on failure */
    }
  }, []);

  const load = useCallback(async () => {
    if (isNew) {
      await loadLookups();
      return;
    }
    setLoading(true);
    setError('');
    try {
      await loadLookups();
      const res = await quoteRequestService.getById(id);
      const data = res.data;
      setTargetType(data.lead_id ? 'lead' : 'customer');
      setForm({
        lead_id: data.lead_id || '',
        customer_id: data.customer_id || '',
        description: data.description || '',
        notes: data.notes || '',
        requested_total_amount: data.requested_total_amount ?? '',
        needed_by_date: data.needed_by_date
          ? String(data.needed_by_date).slice(0, 10)
          : '',
        status: data.status,
        review_notes: data.review_notes || '',
        request_number: data.request_number,
        submitted_at: data.submitted_at,
        created_quote_id: data.created_quote_id,
        created_quote_identifier: data.created_quote_identifier,
        items: (data.items && data.items.length > 0)
          ? data.items.map((it) => ({
              item_type: it.item_type || 'part',
              description: it.description || '',
              quantity: it.quantity ?? 1,
              unit_price: it.unit_price ?? 0,
              unit: it.unit || 'EA',
              notes: it.notes || '',
              part_id: it.part_id,
              service_id: it.service_id,
            }))
          : [emptyItem()],
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, loadLookups]);

  useEffect(() => {
    load();
  }, [load]);

  const itemsTotal = useMemo(
    () => form.items.reduce(
      (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
      0
    ),
    [form.items]
  );

  const updateItem = (index, key, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [key]: value };
      return { ...prev, items };
    });
  };

  const buildPayload = () => {
    const payload = {
      description: form.description,
      notes: form.notes,
      requested_total_amount: form.requested_total_amount !== ''
        ? Number(form.requested_total_amount)
        : itemsTotal,
      needed_by_date: form.needed_by_date || null,
      items: form.items.map((it) => ({
        item_type: it.item_type || 'part',
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        unit: it.unit || 'EA',
        notes: it.notes || null,
        part_id: it.part_id || null,
        service_id: it.service_id || null,
      })),
    };
    if (targetType === 'lead') {
      payload.lead_id = parseInt(form.lead_id, 10);
      payload.customer_id = null;
    } else {
      payload.customer_id = parseInt(form.customer_id, 10);
      payload.lead_id = null;
    }
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (targetType === 'lead' && !form.lead_id) {
        setError('Select a lead');
        setSaving(false);
        return;
      }
      if (targetType === 'customer' && !form.customer_id) {
        setError('Select a customer');
        setSaving(false);
        return;
      }
      const payload = buildPayload();
      if (isNew) {
        const res = await quoteRequestService.create(payload);
        setMessage('Quote request created');
        navigate(`/sales/quote-requests/${res.data.id}`, { replace: true });
      } else {
        await quoteRequestService.update(id, payload);
        setMessage('Saved');
        await load();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editable) {
        const payload = buildPayload();
        if (isNew) {
          const res = await quoteRequestService.create(payload);
          await quoteRequestService.submit(res.data.id);
          navigate(`/sales/quote-requests/${res.data.id}`, { replace: true });
          setSaving(false);
          return;
        }
        await quoteRequestService.update(id, payload);
      }
      await quoteRequestService.submit(id);
      setMessage('Submitted for review');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      await quoteRequestService.withdraw(id);
      setMessage('Withdrawn');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to withdraw');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <SalesPageHeader
        title={isNew ? 'New Quote Request' : `Request #${form.request_number}`}
        subtitle="Describe the opportunity and line items. Admin review turns this into a real Quote."
        actions={
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/quote-requests')}
              sx={{ textTransform: 'none', color: SALES.muted }}
            >
              Back
            </Button>
            {!isNew && (
              <SalesStatusChip status={form.status} metaMap={QUOTE_REQUEST_STATUS_META} />
            )}
            {editable && (
              <Button
                variant="outlined"
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: 'none', borderColor: SALES.line }}
              >
                Save Draft
              </Button>
            )}
            {(isNew || EDITABLE.includes(form.status)) && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
                sx={salesPrimaryButtonSx}
              >
                Submit for Review
              </Button>
            )}
            {!isNew && ['draft', 'submitted', 'rejected'].includes(form.status) && (
              <Button color="warning" onClick={handleWithdraw} sx={{ textTransform: 'none' }}>
                Withdraw
              </Button>
            )}
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {form.status === 'rejected' && form.review_notes && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Rejected: {form.review_notes}
        </Alert>
      )}
      {form.status === 'approved' && form.created_quote_id && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Approved — Quote {form.created_quote_identifier || form.created_quote_id} created
        </Alert>
      )}

      <SalesSectionCard title="Request Details" subtitle="Who this quote is for and what they need">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset" disabled={!editable}>
              <RadioGroup
                row
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value);
                  setForm((prev) => ({ ...prev, lead_id: '', customer_id: '' }));
                }}
              >
                <FormControlLabel value="lead" control={<Radio />} label="New Lead" />
                <FormControlLabel value="customer" control={<Radio />} label="Existing Customer" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {targetType === 'lead' ? (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={!editable}>
                <InputLabel>Lead</InputLabel>
                <Select
                  label="Lead"
                  value={form.lead_id}
                  onChange={(e) => setForm({ ...form, lead_id: e.target.value })}
                >
                  {leads.map((l) => (
                    <MenuItem key={l.id} value={String(l.id)}>
                      #{l.lead_number} — {l.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={!editable}>
                <InputLabel>Customer</InputLabel>
                <Select
                  label="Customer"
                  value={form.customer_id}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                >
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      #{c.customer_number} — {c.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" type="date" label="Needed By" InputLabelProps={{ shrink: true }}
              value={form.needed_by_date}
              onChange={(e) => setForm({ ...form, needed_by_date: e.target.value })}
              disabled={!editable}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Description" multiline minRows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={!editable}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="Notes" multiline minRows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              disabled={!editable}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" label="Requested Total" type="number"
              value={form.requested_total_amount}
              onChange={(e) => setForm({ ...form, requested_total_amount: e.target.value })}
              disabled={!editable}
              helperText={`Line items total: $${itemsTotal.toFixed(2)}`}
            />
          </Grid>
          {!isNew && form.submitted_at && (
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: SALES.muted, fontWeight: 600 }}>Submitted</Typography>
              <Typography>{formatDateDisplay(form.submitted_at)}</Typography>
            </Grid>
          )}
        </Grid>
      </SalesSectionCard>

      <SalesSectionCard
        title="Line Items"
        subtitle="Free-text lines are fine — Admin maps them to catalog parts/services on approval"
        action={
          editable ? (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }))}
              sx={{ textTransform: 'none' }}
            >
              Add Line
            </Button>
          ) : null
        }
      >
        <Table size="small">
          <TableHead sx={salesTableHeadSx}>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell width={90}>Qty</TableCell>
              <TableCell width={110}>Unit Price</TableCell>
              <TableCell width={80}>Unit</TableCell>
              <TableCell width={100}>Total</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {form.items.map((it, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField
                    fullWidth size="small"
                    value={it.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    disabled={!editable}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    disabled={!editable}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number"
                    value={it.unit_price}
                    onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                    disabled={!editable}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={it.unit}
                    onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                    disabled={!editable}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  ${((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
                </TableCell>
                <TableCell>
                  {editable && form.items.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        items: prev.items.filter((_, i) => i !== idx),
                      }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SalesSectionCard>
    </Box>
  );
};

export default SalesQuoteRequestForm;
